import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import {
  CustomFileUpload,
  CustomInputField,
  CustomMultipleFileUpload,
  CustomCheckbox,
  CheckBoxLabel,
  CheckboxContainer,
} from "../FormInputs";
import { getFileName, getFileNameDoc } from "../../../helpers/functions";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../Buttons";
import LockIcon from "@material-ui/icons/Lock";
import GroupIcon from "@material-ui/icons/Group";
import HttpsLockOpen from "@material-ui/icons/LockOpen";
import PublicIcon from "@material-ui/icons/Public";
import {
  Tooltip,
  FormLabel,
  makeStyles,
} from "@material-ui/core";
import DeleteIcons from "@material-ui/icons/Delete";
import { Autocomplete } from "@material-ui/lab";
import CustomSelect from "../CustomSelect/CustomSelect";

const VISIBILITY_OPTIONS = [
  { label: "Select Visibility Type", value: "", disabled: true },
  { label: "Share with all parties", value: "all" },
  { label: "Share with Case Manager & Neutrals", value: "others" },
  { value: "me", mode: "private", label: "Share with My Team" },
  { value: "strictMode", mode: "private", label: "Make Private" },
];

const WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY = {
  all: [
    { label: "Claimant", value: "claimant" },
    { label: "Respondent", value: "respondent" },
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  others: [
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  me: [
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
};

// Shared so the icon next to the Visibility select and the icon shown
// per-uploaded-document always stay in sync with the same rules.
const getVisibilityIcon = (visibilityType) => {
  if (visibilityType === "me") {
    return <GroupIcon style={{ fill: COLORS.BTN_GREEN }} />;
  }
  if (visibilityType === "strictMode") {
    return <LockIcon style={{ fill: COLORS.BTN_GREEN }} />;
  }
  if (visibilityType === "others") {
    return <HttpsLockOpen style={{ fill: COLORS.BTN_GREEN }} />;
  }
  if (visibilityType === "all") {
    return <PublicIcon style={{ fill: COLORS.BTN_GREEN }} />;
  }
  return null;
};

const useStyles = makeStyles((theme) => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "16px",
    backgroundColor: "#ffffff",
    border: "2px solid #8B008B",
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
  },
  customTooltip: {
    backgroundColor: "#D9EAFD",
    color: "#293461",
    fontSize: "12px",
    borderRadius: "8px",
    fontFamily: "openSans-SemiBold, sans-serif",
  },
  customArrow: {
    color: "#D9EAFD",
  },
}));

export default function DocumentUploadModal({
  modal,
  setModal,
  handleUpload = () => {},
  url = "",
  setUrl = () => {},
  handleSuccess = () => {},
  file,
  fileRef,
  data,
  setData,
  shareDefault = false,
  shareDefaultMed = false,
  resolutionKind,
  hasDocumentType,
  menuItems,
  enableWhatsAppAttachmentNotification = false,
}) {
  const [visibility, setVisibility] = useState("");
  const [whatsappNotificationRecipients, setWhatsappNotificationRecipients] =
    useState([]);
  const [type, setType] = useState("");
  const [input, setInput] = useState("");
  const classes = useStyles();

  const recipientOptions =
    WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY[visibility] || [];

  const individualRecipientValues = recipientOptions
    .map((o) => o.value)
    .filter((v) => v !== "all");

  const isAllSelected =
    individualRecipientValues.length > 0 &&
    individualRecipientValues.every((val) =>
      whatsappNotificationRecipients.includes(val),
    );

  const handleVisibilityChange = (nextVisibility) => {
    setVisibility(nextVisibility);
    setWhatsappNotificationRecipients([]);
    if (data && data.length) {
      data.forEach((item) => {
        item.visibilityType = nextVisibility;
        delete item.whatsappNotificationRecipient;
      });
      setData([...data]);
    }
  };

  const handleRecipientChange = (recipientValue) => {
    let nextRecipients;
    if (recipientValue === "all") {
      if (isAllSelected) {
        nextRecipients = [];
      } else {
        nextRecipients = [...individualRecipientValues];
      }
    } else {
      if (whatsappNotificationRecipients.includes(recipientValue)) {
        nextRecipients = whatsappNotificationRecipients.filter(
          (v) => v !== recipientValue,
        );
      } else {
        nextRecipients = [...whatsappNotificationRecipients, recipientValue];
      }
    }

    setWhatsappNotificationRecipients(nextRecipients);
    if (data && data.length) {
      data.forEach((item) => {
        if (nextRecipients.length > 0) {
          item.whatsappNotificationRecipient = nextRecipients;
        } else {
          delete item.whatsappNotificationRecipient;
        }
      });
      setData([...data]);
    }
  };

  useEffect(() => {
    if (modal) {
      setVisibility(shareDefault ? "me" : "");
      setUrl("");
      setType("");
      setData([]);
      setInput("");
      setWhatsappNotificationRecipients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, shareDefault, shareDefaultMed]);

  useEffect(() => {
    if (url) {
      url.map((urlPath) => {
        return data.push({
          url: {
            uploadType: type?.uploadType,
            category: type?.category,
            url: urlPath,
          },
          visibilityType: visibility,
          ...(whatsappNotificationRecipients.length > 0
            ? { whatsappNotificationRecipient: whatsappNotificationRecipients }
            : {}),
        });
      });
      // setType({ category: "", uploadType: "" });
      setUrl("");
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const deleteDocs = (index, item) => {
    const filtered = data.filter((v, idx) => idx !== index);
    setData(filtered);
  };

  return (
    <Modal
      open={modal}
      onClose={() => setModal(false) || setData([])}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <Container>
          <HeadingContainer>
            <Heading>Add Document</Heading>
            <CloseModal
              onClick={() => setModal(false) || setData([])}
              src={require("../../../assets/images/closeModal.svg")}
            />
          </HeadingContainer>
          <AgentFormContainer {...{ resolutionKind }}>
            <div style={{ marginBottom: 10, width: "100%" }}>
              <Label>Visibility</Label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CustomSelect
                  value={visibility}
                  onChange={(e) => handleVisibilityChange(e.target.value)}
                  displayEmpty
                  variant="outlined"
                  fullWidth
                  style={{ marginTop: 6 }}
                  menuItemValues={VISIBILITY_OPTIONS}
                />
                {visibility && (
                  <Tooltip
                    title={
                      VISIBILITY_OPTIONS.find((o) => o.value === visibility)
                        ?.label || ""
                    }
                    placement="top"
                  >
                    <TypeIcon style={{ marginTop: 6 }}>
                      {getVisibilityIcon(visibility)}
                    </TypeIcon>
                  </Tooltip>
                )}
              </div>
            </div>
            {enableWhatsAppAttachmentNotification &&
              recipientOptions.length > 0 && (
                <WhatsAppNotificationSection>
                  <Label>WhatsApp Notification With Attachment</Label>
                  <RecipientOptions>
                    {recipientOptions.map((recipient) => {
                      const isChecked =
                        recipient.value === "all"
                          ? isAllSelected
                          : whatsappNotificationRecipients.includes(
                              recipient.value,
                            );

                      return (
                        <CheckboxContainer key={recipient.value}>
                          <CustomCheckbox
                            id={`wa-upload-${recipient.value}`}
                            inputProps={{ id: `wa-upload-${recipient.value}` }}
                            checked={isChecked}
                            onChange={() =>
                              handleRecipientChange(recipient.value)
                            }
                          />
                          <CheckBoxLabel htmlFor={`wa-upload-${recipient.value}`}>
                            {recipient.label}
                          </CheckBoxLabel>
                        </CheckboxContainer>
                      );
                    })}
                  </RecipientOptions>
                </WhatsAppNotificationSection>
              )}
            <StyledForm />
            {hasDocumentType && (
              <div style={{ marginBottom: 10, width: "100%" }}>
                <Label>Document Type</Label>
                <Autocomplete
                  id="size-small-outlined"
                  size="small"
                  classes={{ noOptions: classes.noOptions }}
                  options={menuItems}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <div ref={params.InputProps.ref}>
                      <CustomInputField
                        {...params}
                        variant="outlined"
                        placeholder="Select notice category..."
                      />
                    </div>
                  )}
                  disableClearable
                  renderGroup={(params) => (
                    <div style={{ padding: "8px 0" }}>
                      <div
                        style={{
                          padding: "10px 16px",
                          fontWeight: "700",
                          fontSize: "11px",
                          color: "#0f766e",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          backgroundColor: "#f0fdf4",
                          borderRadius: "6px",
                          margin: "4px 8px",
                        }}
                      >
                        {params.group}
                      </div>
                      <div
                        style={{
                          padding: "4px 0",
                          color: COLORS.COLOR_DARK,
                          fontFamily: theme.fonts.primaryFontSemiBold,
                        }}
                      >
                        {params.children}
                      </div>
                    </div>
                  )}
                  noOptionsText={
                    <div variant="body2" color="textSecondary">
                      No Document type found
                    </div>
                  }
                  onChange={(event, newValue) =>
                    setType({
                      category: newValue?.category,
                      uploadType: newValue?.name,
                    })
                  }
                />
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <Label>Document</Label>
              <CustomFileUpload
                error={false}
                setFile={(file) => handleUpload(file)}
                style={{ marginTop: 6, width: "100%" }}
                file={{ name: getFileNameDoc(url) }}
                value={file}
                ref={fileRef}
                fileUrl={url}
                multiple="multiple"
                type="file"
                accept=".pdf, .mp3, .mp4, .docx, .mov, .doc, .xlsx, image/*"
                // accept=".pdf"
              />
            </div>
            {type === "Others" ? (
              <div>
                <Label>{type} (Optional)</Label>
                <CustomInputField
                  variant="outlined"
                  style={{ marginTop: 6, width: "100%" }}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
              </div>
            ) : null}
            <div>
              {data
                ? data.map((item, index) => (
                    <>
                      <div style={{ display: "flex" }}>
                        <CustomMultipleFileUpload
                          error={false}
                          style={{ marginTop: 6 }}
                          file={{ name: getFileName(item?.url?.url) }}
                          value={item?.url?.url}
                          ref={fileRef}
                          fileUrl={item?.url?.url}
                          setUrl={setUrl}
                          key={index}
                          type="file"
                          accept=".pdf, .mp3, .mp4, .docx, .mov, .doc, .xlsx, image/*"
                        />{" "}
                        <div style={{ display: "flex" }}>
                          <TypeIcon>
                            <Tooltip
                              title={item?.url?.uploadType}
                              placement="top"
                            >
                              {getVisibilityIcon(item?.visibilityType)}
                            </Tooltip>
                          </TypeIcon>{" "}
                          <DeleteIcon
                            onClick={() => {
                              deleteDocs(index, item);
                            }}
                            key={index}
                          >
                            <DeleteIcons style={{ color: COLORS.LOGOUT_RED }} />
                          </DeleteIcon>
                        </div>
                      </div>
                    </>
                  ))
                : ""}
            </div>
            <StyledFormElementsWrapper
              style={{ marginBottom: 13, fontSize: "18px" }}
            >
              <FormLabel
                style={{
                  fontFamily: theme.fonts.primaryFontSemiBold,
                  fontSize: 12,
                  color: COLORS.COLOR_DARK,
                }}
              >
                Note: For redressal of Securities Market disputes, conducted
                under the aegis of SEBI, it is recommended to share the
                documents only in Public Sharing mode
              </FormLabel>
            </StyledFormElementsWrapper>
            <ButtonContainer>
              <PrimaryOutlinedCloseCTAButton
                style={{ width: "45%" }}
                onClick={(e) => {
                  e.preventDefault();
                  setModal(false);
                  setUrl("");
                  setData([]);
                }}
              >
                Cancel
              </PrimaryOutlinedCloseCTAButton>
              <PrimaryCTAButton
                //   disabled={!url || (shareDefault && !type)}
                disabled={!data?.length}
                style={{ width: "45%" }}
                onClick={(e) => {
                  e.preventDefault();
                  const payloadData = data.map((item) => {
                    const doc = {
                      ...item,
                      visibilityType: visibility,
                    };
                    if (whatsappNotificationRecipients.length > 0) {
                      doc.whatsappNotificationRecipient =
                        whatsappNotificationRecipients;
                    } else {
                      delete doc.whatsappNotificationRecipient;
                    }
                    return doc;
                  });
                  handleSuccess(payloadData);
                }}
              >
                Upload Document
              </PrimaryCTAButton>
            </ButtonContainer>
          </AgentFormContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
}

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 516px;
  }
`;

const HeadingContainer = styled.div`
  padding-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 30px;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const AgentFormContainer = styled.p`
  padding: 24px 15px 30px 15px;
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.71;
  text-align: justify;
  backface-visibility: hidden;
  max-height: ${({ resolutionKind }) =>
    resolutionKind === "arbitration" ? "500px" : "436px"};
  overflow: auto;
  z-index: -1px;
  overflow-x: hidden;
  @media ${theme?.breakpoints?.sm_up} {
    padding-top: 24px;
    padding-left: 40px;
    padding-right: 40px;
    padding-bottom: 30px;
  }
`;

const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  line-height: 1.7;
  color: ${COLORS.INPUT_LABEL};
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 40px;
  justify-content: space-around;
`;

const DeleteIcon = styled.div`
  margin: 20px 5px 0px;
  cursor: pointer;
`;

const TypeIcon = styled.div`
  margin: 20px 5px 0px;
  cursor: pointer;
`;

const StyledForm = styled.div`
  margin-bottom: 10px;
`;

const WhatsAppNotificationSection = styled.div`
  margin: 4px 0 14px;
`;

const RecipientOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 2px;

  .MuiFormControlLabel-root {
    margin-right: 0;
  }

  .MuiFormControlLabel-label {
    color: ${COLORS.COLOR_DARK};
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 13px;
  }
`;

const StyledFormElementsWrapper = styled.div`
  margin-bottom: 28px;
  max-width: 436px;
  & .link {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
  }
  & .template {
    margin-top: 0px;
  }
  & .waring-msg {
    color: ${COLORS.WARNING_COLOR};
    font-size: 10px;
    white-space: nowrap;
  }
  & .mediator-type {
    .MuiFormGroup-root {
      display: flex;
      flex-direction: column !important;
      & .MuiFormControlLabel-root {
        padding: 10px 0px;
      }
    }
  }
  @media ${theme?.breakpoints?.sm_down} {
    & .template {
      margin-top: 15px;
    }
  }
`;
